// ── 계약 변경이력 모달 ─────────────────────────────────────────────────────────
// contracts.id 기준으로 이력을 불러와 시간순으로 표시
import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useContractApi } from './contractApi';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  overlayStyle, confirmBoxStyle,
  confirmFooterStyle, confirmCancelBtnStyle,
} from './modalStyles';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../adminlayout/designStandard';

export function ContractLogModal({ open, contractId, companyName, onClose }) {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const api = useContractApi();
  useBodyScrollLock(open);
  const overlayCloseProps = useOverlayClickClose(onClose);

  // 모달이 열릴 때마다 이력 재조회
  useEffect(() => {
    if (!open || !contractId) return;
    setLoading(true);
    apiClient.get(`${api.contracts}/${contractId}/logs`).then((res) => {
      setLoading(false);
      if (res.ok) setLogs(Array.isArray(res.data?.data) ? res.data.data : []);
      else setLogs([]);
    });
  }, [open, contractId, api.contracts]);

  if (!open) return null;

  // 액션 한글 변환
  const actionLabel = (action) => {
    if (action === 'create') return { text: '등록', color: '#16a34a', bg: '#dcfce7' };
    if (action === 'update') return { text: '수정', color: '#2563eb', bg: '#eff6ff' };
    if (action === 'cancel') return { text: '취소', color: '#d97706', bg: '#fef3c7' };
    if (action === 'delete') return { text: '삭제', color: '#dc2626', bg: '#fee2e2' };
    return { text: action, color: 'var(--text-muted)', bg: 'var(--bg-subtle)' };
  };

  // JSONB 값을 사람이 읽기 쉬운 형태로 변환
  const FIELD_LABELS = {
    contract_date:        '계약일자',
    contract_items:       '계약상품',        // 신형 (contract_items 배열)
    contract_ticket_type: '계약상품(구)',     // 구형 이력 호환용
    service_ticket_type:  '서비스상품(구)',   // 구형 이력 호환용
    service_ticket_count: '서비스수량(구)',   // 구형 이력 호환용
    contract_status:      '계약상태',
    courier:              '택배사',
    tracking_number:      '송장번호',
    delivery_items:       '다중배송',
    company_name:         '상호',
    memo:                 '비고',
    phone:                '전화',
    mobile_phone:         '휴대폰',
    address:              '주소',
    feedback:             '피드백',
    user_name:            '상담자',
    is_reorder:           '재주문',
    consultant_share_rate: '상담자 배분율(%)',  // 정산서 배분율 저장 기록(2026-10-12)
    company_code:         '회사',              // 만들 때 정한 계약 회사(2026-10-13)
  };

  // null/undefined/빈문자 → 표시용 문자열 변환
  function displayVal(v) {
    if (v == null || v === '') return null;
    if (Array.isArray(v)) {
      // contract_items 배열 (ticketTypeName 필드 존재 여부로 구분)
      if (v.length > 0 && v[0]?.ticketTypeName != null) {
        return v.filter((i) => !i.isService).map((i) => `${i.ticketTypeName}×${i.quantity}`).join(', ') || null;
      }
      // delivery_items 배열 (courier, tracking_number, 직접수령 줄은 is_pickup)
      return v.map((i) => (i.is_pickup ? '직접수령' : [i.courier, i.tracking_number].filter(Boolean).join(' '))).filter(Boolean).join(' / ') || null;
    }
    return String(v);
  }

  // 이력 항목 렌더링: 등록 — new_values 나열, 수정/취소/삭제 — 변경된 필드만 before→after
  function renderDiff(oldVals, newVals, action) {
    // 등록: new_values에 값 있는 필드만 나열
    if (action === 'create' || (!oldVals && newVals)) {
      const entries = Object.entries(newVals || {}).filter(([, v]) => displayVal(v) != null);
      if (!entries.length) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>내용 없음</span>;
      return entries.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 3 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, minWidth: 72 }}>{FIELD_LABELS[k] || k}</span>
          <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600, wordBreak: 'break-all' }}>{displayVal(v)}</span>
        </div>
      ));
    }
    // 삭제: old_values에 값 있는 필드만 나열
    if (action === 'delete' || (!newVals && oldVals)) {
      const entries = Object.entries(oldVals || {}).filter(([, v]) => displayVal(v) != null);
      if (!entries.length) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>내용 없음</span>;
      return entries.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 3 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, minWidth: 72 }}>{FIELD_LABELS[k] || k}</span>
          <span style={{ fontSize: 12, color: '#dc2626', textDecoration: 'line-through', wordBreak: 'break-all' }}>{displayVal(v)}</span>
        </div>
      ));
    }
    // 수정/취소: new_values의 key 기준으로 실제 변경된 필드만 before→after 표시
    const keys    = Object.keys(newVals || {});
    const changed = keys.filter((k) => {
      const ov = displayVal(oldVals?.[k]); const nv = displayVal(newVals?.[k]);
      return (ov || '') !== (nv || '');
    });
    if (!changed.length) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>변경된 필드 없음</span>;
    return changed.map((k) => {
      const ov = displayVal(oldVals?.[k]); const nv = displayVal(newVals?.[k]);
      return (
        <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, minWidth: 72 }}>{FIELD_LABELS[k] || k}</span>
          {ov != null && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', textDecoration: 'line-through', wordBreak: 'break-all' }}>{ov}</span>
          )}
          {ov != null && nv != null && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>→</span>
          )}
          <span style={{ fontSize: 12, color: nv != null ? 'var(--text)' : 'var(--text-muted)', fontWeight: nv != null ? 600 : 400, wordBreak: 'break-all' }}>
            {nv != null ? nv : '(삭제됨)'}
          </span>
        </div>
      );
    });
  }

  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={{ ...confirmBoxStyle, maxWidth: 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 — 검정 표준 밴드 (2026-08-31) */}
        <div style={stdModalHeaderBar(16)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>⏰</div>
            <span style={stdModalHeaderTitle}>변경이력{companyName ? ` — ${companyName}` : ''}</span>
          </div>
          <button onClick={onClose} style={stdModalCloseBtn}>✕</button>
        </div>
        {/* 본문 */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 16px' }}>
          {loading && <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>불러오는 중...</div>}
          {!loading && logs.length === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>변경이력이 없습니다.</div>
          )}
          {!loading && logs.map((log) => {
            const al = actionLabel(log.action);
            return (
              <div key={log.id} style={{ borderBottom: '1px solid var(--bg-subtle)', paddingBottom: 12, marginBottom: 12 }}>
                {/* 타임라인 헤더 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: al.color, background: al.bg, borderRadius: 5, padding: '2px 8px' }}>
                    {al.text}
                  </span>
                  {log.source && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', borderRadius: 5, padding: '2px 7px', border: '1px solid #e9d5ff' }}>
                      {log.source}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>
                    {log.actor_name || log.actor_login_id || '(알 수 없음)'}
                  </span>
                  {log.actor_login_id && log.actor_name && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({log.actor_login_id})</span>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{log.created_at_kst}</span>
                </div>
                {/* 변경 내용 */}
                <div style={{ paddingLeft: 8, borderLeft: `3px solid ${al.color}`, marginLeft: 4 }}>
                  {renderDiff(log.old_values, log.new_values, log.action)}
                </div>
              </div>
            );
          })}
        </div>
        {/* 푸터 */}
        <div style={{ ...confirmFooterStyle, justifyContent: 'flex-end' }}>
          <button style={confirmCancelBtnStyle} onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
